import '@/styles/blog.css'
import { Head, Link } from '@inertiajs/react'
import { MarketingLayout } from '@/layouts/MarketingLayout'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, ArrowRight, Calendar, Clock } from 'lucide-react'

interface Post {
  id: number
  title: string
  slug: string
  excerpt: string | null
  author_name: string | null
  cover_image_url: string | null
  published_at: string
  reading_time_min: number | null
  tags: Array<{ name: string; slug: string }>
  body_html: string
}

interface RelatedPost {
  id: number
  title: string
  slug: string
  excerpt: string | null
  cover_image_url: string | null
  published_at: string
  reading_time_min: number | null
  tags: Array<{ name: string; slug: string }>
}

interface Props {
  post: Post
  related: RelatedPost[]
  seo: { title: string; description: string; image: string; canonical_url: string }
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export default function Show({ post, related, seo }: Props) {
  return (
    <MarketingLayout appUrl="">
      <Head title={`${seo.title} — Tippy`}>
        {seo.description && <meta name="description" content={seo.description} />}
        {seo.title && <meta property="og:title" content={seo.title} />}
        {seo.description && <meta property="og:description" content={seo.description} />}
        <meta property="og:image" content={seo.image} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={seo.canonical_url} />
      </Head>

      <article className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-28 pb-16 lg:pt-36 lg:pb-24">
        {/* Back */}
        <div className="mb-8">
          <Link
            href="/blog"
            className="inline-flex min-h-10 items-center rounded-xl px-3 text-sm font-medium text-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 active:bg-[#b9ff4a] active:text-black"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Blog
          </Link>
        </div>

        {/* Header */}
        <header className="mb-8">
          {post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((t) => (
                <Link key={t.slug} href={`/blog/tag/${t.slug}`}>
                  <Badge variant="secondary">{t.name}</Badge>
                </Link>
              ))}
            </div>
          )}

          <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">{post.title}</h1>

          <div className="flex items-center gap-4 text-muted-foreground text-sm">
            {post.author_name && <span>{post.author_name}</span>}
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(post.published_at)}
            </span>
            {post.reading_time_min && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.reading_time_min} min de lectura
              </span>
            )}
          </div>
        </header>

        {/* Cover image */}
        {post.cover_image_url && (
          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={post.cover_image_url}
              alt={post.title}
              className="w-full max-h-[500px] object-cover"
            />
          </div>
        )}

        {/* Body */}
        <div
          className="blog-content mb-16 max-w-[70ch]"
          dangerouslySetInnerHTML={{ __html: post.body_html }}
        />

        {/* Related posts */}
        {related.length > 0 && (
          <section className="border-t border-border pt-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Artículos relacionados</h2>
            <div className="border-y border-border">
              {related.map((r) => (
                <Link
                  key={r.id}
                  href={`/blog/${r.slug}`}
                  className="group grid gap-4 border-b border-border py-5 transition-colors last:border-b-0 hover:bg-secondary/55 focus-visible:rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 active:bg-[#b9ff4a]/35 sm:grid-cols-[6rem_minmax(0,1fr)_auto] sm:items-center sm:px-2"
                >
                  {r.cover_image_url && (
                    <img
                      src={r.cover_image_url}
                      alt=""
                      className="aspect-[4/3] w-24 rounded-xl object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className={r.cover_image_url ? '' : 'sm:col-start-1 sm:col-span-2'}>
                    <h3 className="max-w-[42ch] font-semibold leading-snug text-foreground decoration-2 underline-offset-4 group-hover:underline">
                      {r.title}
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs font-medium text-muted-foreground">
                      <time dateTime={r.published_at}>{formatDate(r.published_at)}</time>
                      {r.reading_time_min && <span>{r.reading_time_min} min</span>}
                    </div>
                  </div>
                  <ArrowRight className="hidden h-4 w-4 transition-transform group-hover:translate-x-1 sm:block" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </MarketingLayout>
  )
}
